import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import NoSSR from '@material-ui/core/NoSsr';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import useStores from 'shared/hooks/use-stores';
import useApolloClient from 'shared/hooks/use-apollo-client';
import { useCheckout } from 'checkout/context';
import useErnie from 'shared/hooks/use-ernie';
import { mediaQueries } from 'shared/styles';
import DeliveryErrorModal from 'checkout/modals/delivery-validation-error';
import Header from 'checkout/components/header';
import Sections from 'checkout/components/sections';
import Confirmation from 'checkout/components/confirmation';
import onCheckoutMount from 'src/checkout/actions/on-checkout-mount';
import { useDutchiePay } from 'src/payments/hooks';
import useLogRocket from 'src/hooks/use-logrocket';
import usePlaidIframeObserver from 'src/payments/hooks/use-plaid-iframe-observer';
import { useDispensaryDisclaimer } from 'src/dispensary/hooks/use-dispensary-disclaimer';
import RichTextHtmlWrapper from 'shared/components/rich-text-html-wrapper';

function CheckoutContainer() {
  const { Cart, User, UI } = useStores();
  const apolloClient = useApolloClient();
  const showErnie = useErnie();
  const CheckoutCtx = useCheckout();
  const { order } = Cart;
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { isDutchiePayEnabledForDispo, isEnrolledDutchiePay } = useDutchiePay();
  const { track, identify } = useLogRocket();
  const { disclaimerIsBlank, disclaimerTextHtml } = useDispensaryDisclaimer();

  useEffect(() => {
    onCheckoutMount({ UI, Cart, User, apolloClient, showErnie, CheckoutCtx });

    if (isDutchiePayEnabledForDispo) {
      identify(order?.checkoutToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (User.isLoggedIn) {
      if (isDutchiePayEnabledForDispo && isEnrolledDutchiePay) {
        // track checkout with dutchiePay enabled and user logged in and enrolled in DutchiePay
        track(`DutchiePay_logged_in_enrolled`);
      } else {
        // track checkout with dutchiePay enabled and user logged in and NOT enrolled in DutchiePay
        track(`DutchiePay_logged_in_unenrolled`);
      }
    } else if (!User.isLoggedIn) {
      if (isDutchiePayEnabledForDispo) {
        // track checkout with dutchiePay enabled
        track(`DutchiePay_logged_out`);
      }
      DutchiePayEnrollment.resetEnrollment();
    }
  }, [
    User.isLoggedIn,
    User.email,
    User.id,
    User.profile,
    User.profile.firstName,
    User.profile.lastName,
    User.profile.phone,
    DutchiePayEnrollment,
    isDutchiePayEnabledForDispo,
    isEnrolledDutchiePay,
    track,
  ]);

  usePlaidIframeObserver();

  return (
    <>
      {order.cart && (
        <>
          <Container>
            <CheckoutForm>
              <Header />
              <Sections />
            </CheckoutForm>

            <Overview>
              <Confirmation />
            </Overview>
          </Container>

          {!disclaimerIsBlank && (
            <Disclaimer>
              <RichTextHtmlWrapper html={disclaimerTextHtml} />
            </Disclaimer>
          )}
        </>
      )}

      <NoSSR>{CheckoutCtx.viewingDeliveryErrorModal && <DeliveryErrorModal />}</NoSSR>
    </>
  );
}

export default observer(CheckoutContainer);

const Container = styled.div`
  display: flex;
  justify-content: center;
  max-width: 990px;
  margin: 0 auto;

  @media ${mediaQueries.desktop} {
    display: block;
    max-width: none;
    margin: 0;
  }
`;

const CheckoutForm = styled.div`
  flex: 1 0 0%;
  max-width: 640px;
  min-width: 0;
  padding-left: 0;
  padding-right: 40px;
  padding-bottom: 80px;

  @media ${mediaQueries.desktop} {
    margin: 0 auto;
    padding-right: 0;
    padding-bottom: 30px;
  }

  @media ${mediaQueries.tablet} {
    padding-bottom: 20px;
  }

  @media ${mediaQueries.largePhone} {
    padding-right: 0;
    padding-left: 0;
    max-width: 450px;
    width: 100%;
  }
`;

const Overview = styled.div`
  flex: 0 0 307px;
  padding-right: 0;

  @media ${mediaQueries.largePhone} {
    width: 100%;
  }
`;

const Disclaimer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.greyBorder};
  color: #667882;
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  font-size: 12px;
  line-height: ${18 / 12};
  margin: 0 auto;
  max-width: 800px;
  padding: 4px 20px 80px;
  text-align: center;
  width: 100%;

  @media ${mediaQueries.largePhone} {
    margin-top: 80px;
  }
`;
